export const LineItemType = Object.freeze({
  ticket: 'ticket',
});

export interface CreateLineItemParams {
  type: typeof LineItemType;
  id: string;
  name: string;
  description: string | null;
  unitPrice: number;
  quantity: number;
  bulkDiscountAmount?: number;
  couponDiscountAmount?: number;
  appliedCoupon?: Record<string, any> | null;
  appliedBulkDiscountRule?: Record<string, any> | null;
  currency: string;
  /** Percentage, not a fraction: 20 means 20% — same units as
   *  `totalTaxAmounts[].percentage`, which is what the only caller passes. */
  taxRate?: number;
}

export interface LineItem {
  type: typeof LineItemType;
  id: string;
  name: string;
  description: string | null;
  unitPrice: number;
  quantity: number;
  appliedCoupon?: Record<string, any> | null;
  appliedBulkDiscountRule?: Record<string, any> | null;
  taxRate?: number;
  taxAmount?: number;
  subtotal: number;
  bulkDiscountAmount?: number;
  couponDiscountAmount?: number;
  totalDiscountAmount: number; // bulk discount + coupon discount
  total: number; // final amount customer pays for this line (after discount + tax) before order level discounts and coupons
  currency: string;
}

export function createCartLineItem(params: CreateLineItemParams): LineItem {
  const {
    type,
    id,
    name,
    description,
    unitPrice,
    quantity,
    bulkDiscountAmount = 0,
    couponDiscountAmount = 0,
    appliedCoupon = null,
    appliedBulkDiscountRule = null,
    taxRate = 0,
    currency,
  } = params;

  const subtotal = quantity * unitPrice;
  const totalDiscountAmount = bulkDiscountAmount + couponDiscountAmount;
  const taxableAmount = subtotal - totalDiscountAmount;
  // taxRate is a percentage — divide before applying, or a 20% rate multiplies
  // the line by 20 instead of taking a fifth of it. Rounded because line
  // amounts are whole minor units.
  const taxAmount = taxableAmount > 0 ? Math.round((taxableAmount * taxRate) / 100) : 0;
  const total = taxableAmount + taxAmount;

  return {
    type,
    id,
    name,
    description,

    quantity,
    unitPrice,
    subtotal,

    bulkDiscountAmount,
    couponDiscountAmount,
    totalDiscountAmount,

    taxRate,
    taxAmount,

    total,

    currency,

    appliedCoupon: appliedCoupon ?? null,
    appliedBulkDiscountRule: appliedBulkDiscountRule ?? null,
  };
}
